import React, { forwardRef, useEffect, useImperativeHandle, useRef, useState } from "react";
import { Box, IconButton, Typography, CircularProgress, Tooltip, Divider, InputBase, useMediaQuery } from "@mui/material";
import { Add, ChatBubbleOutline, Mic, ContentCopy, Check } from "@mui/icons-material";
import { colors } from "../../theme/designSystem";
import { useAuth } from "../../contexts/AuthContext";
import { ActionChips, AuditNotice, ComposerFrame, ComposerIconButton, SendButton, SpeakerLabel } from "../assistants/AssistantUi";
import { aiChatService, MyAgent, ChatMessage, ConversationSummary } from "../../services/aiChatService";

// Conversa com um agente de IA: o modelo de chat de IA original (histórico por título, envio ao
// agente, markdown, ações sugeridas, ditado por voz). Vive dentro do chat do tenant como um
// contato; a casca (lista de contatos, cabeçalho, FAB) fica no ChatLauncher.

// Rótulo amigável da página atual (contexto inicial da conversa). Fallback: a própria chave.
const PAGE_LABELS: Record<string, string> = {
  dashboard: "Painel",
  schedule: "Agenda",
  patients: "Pacientes",
  "patient-register": "Cadastro de paciente",
  "report-schedule-analysis": "Relatório de atendimentos",
  "report-patient-flow": "Novos pacientes e evasão",
  "report-cashflow": "Fluxo de caixa",
  "report-dre": "DRE",
  rooms: "Salas",
  "entity-users": "Usuários",
  "professional-availability": "Disponibilidade do profissional",
  "professional-contracts": "Contratos do profissional",
  "crm-funnel": "Pipeline do CRM",
  "whatsapp-channels": "Canais de WhatsApp",
  "crm-settings": "Configuração de pipeline",
  "crm-campaigns": "Campanhas do CRM",
};
const pageLabel = (page: string): string | null => PAGE_LABELS[page] ?? null;

export const formatWhen = (iso: string): string => {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleString("pt-BR", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" });
};

// URL segura para link: http/https absoluto ou link interno (/, ?, #). Bloqueia javascript:/data: etc.
const safeHref = (url: string): string | null => {
  const u = url.trim();
  return /^https?:\/\//i.test(u) || /^[/?#]/.test(u) ? u : null;
};

const linkStyle: React.CSSProperties = { color: "#03B4C6", textDecoration: "underline", wordBreak: "break-word" };

// --- Markdown leve (negrito, itálico, código, links, listas, régua, títulos) sem dependência externa ---
const renderInline = (text: string): React.ReactNode[] => {
  const parts: React.ReactNode[] = [];
  // ordem importa: link markdown antes da URL solta (para não capturar a url de dentro do "](url)").
  const regex = /(\*\*[^*]+\*\*|\*[^*]+\*|`[^`]+`|\[[^\]]+\]\([^)]+\)|https?:\/\/[^\s)]+)/g;
  let last = 0;
  let key = 0;
  let match: RegExpExecArray | null;
  while ((match = regex.exec(text)) !== null) {
    if (match.index > last) parts.push(text.slice(last, match.index));
    const tok = match[0];
    if (tok.startsWith("**")) {
      parts.push(<strong key={key++}>{tok.slice(2, -2)}</strong>);
    } else if (tok.startsWith("`")) {
      parts.push(
        <code key={key++} style={{ background: "#eef0f2", borderRadius: 4, padding: "0 4px", fontSize: "0.8em" }}>
          {tok.slice(1, -1)}
        </code>
      );
    } else if (tok.startsWith("[")) {
      const m = /^\[([^\]]+)\]\(([^)]+)\)$/.exec(tok);
      const href = m ? safeHref(m[2]) : null;
      if (m && href) {
        parts.push(
          <a key={key++} href={href} target="_blank" rel="noopener noreferrer" style={linkStyle}>
            {m[1]}
          </a>
        );
      } else {
        parts.push(tok); // url insegura → texto puro (não vira link)
      }
    } else if (tok.startsWith("http")) {
      parts.push(
        <a key={key++} href={tok} target="_blank" rel="noopener noreferrer" style={linkStyle}>
          {tok}
        </a>
      );
    } else {
      parts.push(<em key={key++}>{tok.slice(1, -1)}</em>);
    }
    last = match.index + tok.length;
  }
  if (last < text.length) parts.push(text.slice(last));
  return parts;
};

// Tabela GFM: linha com pipes + separador (|---|---|). Cabe no painel estreito via scroll horizontal.
const splitCells = (line: string): string[] => {
  let t = line.trim();
  if (t.startsWith("|")) t = t.slice(1);
  if (t.endsWith("|")) t = t.slice(0, -1);
  return t.split("|").map((c) => c.trim());
};
const isTableSeparator = (line: string): boolean =>
  /^\s*\|?\s*:?-{2,}:?\s*(\|\s*:?-{2,}:?\s*)+\|?\s*$/.test(line);
const isTableRow = (line: string): boolean => line.includes("|") && line.trim().length > 0;

// Exportado para o painel da entrevista usar a MESMA renderizacao: duas caixas de
// conversa no mesmo sistema nao devem formatar texto de jeitos diferentes.
export const renderMarkdown = (content: string): React.ReactNode => {
  const lines = content.split(/\r?\n/);
  const blocks: React.ReactNode[] = [];
  let list: string[] = [];
  let key = 0;

  const flushList = () => {
    if (list.length === 0) return;
    const items = list;
    list = [];
    blocks.push(
      <Box key={`ul-${key++}`} component="ul" className="c4-text" sx={{ m: 0, pl: 2.5, display: "flex", flexDirection: "column", gap: 0.25 }}>
        {items.map((it, i) => (
          <li key={i}>{renderInline(it)}</li>
        ))}
      </Box>
    );
  };

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    const t = line.trim();

    // Tabela: cabeçalho + separador na linha seguinte. Consome as linhas de dados contíguas.
    if (isTableRow(line) && i + 1 < lines.length && isTableSeparator(lines[i + 1])) {
      flushList();
      const header = splitCells(line);
      const rows: string[][] = [];
      let j = i + 2;
      while (j < lines.length && isTableRow(lines[j]) && !isTableSeparator(lines[j])) {
        rows.push(splitCells(lines[j]));
        j++;
      }
      blocks.push(
        <Box key={`tbl-${key++}`} sx={{ overflowX: "auto", my: 0.5 }}>
          <Box component="table" sx={{ borderCollapse: "collapse", width: "100%", fontSize: "0.82em" }}>
            <Box component="thead">
              <Box component="tr">
                {header.map((c, ci) => (
                  <Box component="th" key={ci} sx={{ border: "1px solid #e0e0e0", px: 0.75, py: 0.4, textAlign: "left", fontWeight: 700, background: "#f5f6f7", whiteSpace: "nowrap" }}>
                    {renderInline(c)}
                  </Box>
                ))}
              </Box>
            </Box>
            <Box component="tbody">
              {rows.map((r, ri) => (
                <Box component="tr" key={ri}>
                  {header.map((_, ci) => (
                    <Box component="td" key={ci} sx={{ border: "1px solid #eee", px: 0.75, py: 0.4, textAlign: "left", verticalAlign: "top" }}>
                      {renderInline(r[ci] ?? "")}
                    </Box>
                  ))}
                </Box>
              ))}
            </Box>
          </Box>
        </Box>
      );
      i = j - 1;
      continue;
    }

    if (/^(-{3,}|\*{3,}|_{3,})$/.test(t)) {
      flushList();
      blocks.push(<Divider key={`hr-${key++}`} className="c4-text" sx={{ my: 0.75 }} />);
      continue;
    }
    const bullet = t.match(/^[-*]\s+(.*)/);
    if (bullet) {
      list.push(bullet[1]);
      continue;
    }
    flushList();
    if (t === "") {
      blocks.push(<Box key={`sp-${key++}`} sx={{ height: 6 }} />);
      continue;
    }
    const heading = t.match(/^(#{1,6})\s+(.*)/);
    if (heading) {
      blocks.push(
        <Box key={`h-${key++}`} className="c4-text" sx={{ fontWeight: 700, mt: 0.5 }}>
          {renderInline(heading[2])}
        </Box>
      );
      continue;
    }
    blocks.push(<Box key={`p-${key++}`} className="c4-text">{renderInline(line)}</Box>);
  }
  flushList();
  return <>{blocks}</>;
};

// Extrai o bloco ```acoes (ações sugeridas pelo agente) do texto: devolve o texto sem o bloco + as ações.
export const parseAssistant = (content: string): { text: string; actions: string[] } => {
  const fence = content.match(/```(?:acoes|ações|actions)\s*\n([\s\S]*?)```/i);
  if (!fence) return { text: content, actions: [] };
  const actions = fence[1]
    .split(/\r?\n/)
    .map((l) => l.replace(/^\s*(?:[-*•]\s+|\d+[.)]\s+)/, "").trim())
    .filter((l) => l.length > 0)
    .slice(0, 6);
  return { text: content.replace(fence[0], "").trim(), actions };
};

export type AgentView = "conversations" | "chat";

export interface AgentConversationHandle {
  startNew: () => void;
  /** Volta um nível dentro do agente; devolve false quando já está na lista (a casca decide o resto). */
  back: () => boolean;
}

interface Props {
  agent: MyAgent;
  currentPage: string;
  clinicName: string;
  onViewChange?: (view: AgentView) => void;
}

const AgentConversation = forwardRef<AgentConversationHandle, Props>(({ agent, currentPage, clinicName, onViewChange }, ref) => {
  const isMobile = useMediaQuery("(max-width:767.98px)");
  const { user } = useAuth();
  const userInitial = (user?.email || "V").trim().charAt(0).toUpperCase();
  const [view, setViewState] = useState<AgentView>("conversations");
  const [conversations, setConversations] = useState<ConversationSummary[]>([]);
  // truncated: só existe na resposta ao vivo (o histórico salvo não guarda o flag).
  const [messages, setMessages] = useState<(ChatMessage & { truncated?: boolean })[]>([]);
  const [conversationId, setConversationId] = useState<string | null>(null);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // conversa encerrada por inatividade: mostra o atalho de nova conversa junto do aviso.
  const [expired, setExpired] = useState(false);
  const [copiedIndex, setCopiedIndex] = useState<number | null>(null);
  const listEndRef = useRef<HTMLDivElement | null>(null);
  const lastMsgRef = useRef<HTMLDivElement | null>(null);
  const inputRef = useRef<HTMLInputElement | HTMLTextAreaElement | null>(null);
  // Como alinhar a rolagem no próximo render: "bottom" (mostra o fim) ou "replyTop"
  // (alinha o TOPO da resposta do agente, para ler do começo em respostas longas).
  const pendingScroll = useRef<"bottom" | "replyTop">("bottom");

  const setView = (v: AgentView) => {
    setViewState(v);
    onViewChange?.(v);
  };

  const loadConversations = async (agentId: string) => {
    try {
      // Chat comum: a lista de sempre, 30 dias; as conversas das áreas ficam no histórico de cada área (RN-573)
      setConversations(await aiChatService.conversations(agentId, { days: 30 }));
    } catch {
      setConversations([]);
    }
  };

  useEffect(() => {
    setConversationId(null);
    setMessages([]);
    setError(null);
    setExpired(false);
    setView("conversations");
    loadConversations(agent.id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [agent.id]);

  useEffect(() => {
    if (view !== "chat") return;
    if (!sending && pendingScroll.current === "replyTop" && lastMsgRef.current) {
      lastMsgRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
    } else {
      listEndRef.current?.scrollIntoView({ behavior: "smooth", block: "end" });
    }
    pendingScroll.current = "bottom";
  }, [messages, sending, view]);

  const openConversation = async (conv: ConversationSummary) => {
    setConversationId(conv.id);
    setMessages([]);
    setError(null);
    setExpired(false);
    setView("chat");
    try {
      setMessages(await aiChatService.messages(conv.id));
    } catch {
      setError("Não foi possível carregar as mensagens.");
    }
  };

  const startNew = () => {
    setConversationId(null);
    setMessages([]);
    setError(null);
    setExpired(false);
    setView("chat");
  };

  useImperativeHandle(ref, () => ({
    startNew,
    back: () => {
      if (view === "chat") {
        loadConversations(agent.id);
        setView("conversations");
        return true;
      }
      return false;
    },
  }));

  const send = async () => {
    const text = input.trim();
    if (!text || sending) return;
    setInput("");
    setError(null);
    setExpired(false);
    setMessages((prev) => [...prev, { role: "user", content: text, createdAt: new Date().toISOString() }]);
    setSending(true);
    try {
      const result = await aiChatService.send(agent.id, conversationId, text, pageLabel(currentPage));
      setConversationId(result.conversationId);
      pendingScroll.current = "replyTop";
      setMessages((prev) => [
        ...prev,
        { role: "assistant", content: result.reply, createdAt: new Date().toISOString(), truncated: result.truncated },
      ]);
    } catch (e: unknown) {
      const data = (e as { response?: { data?: { code?: string; description?: string } } })?.response?.data;
      setExpired(data?.code === "AiAgent.ConversationExpired");
      setError(data?.description || "Não foi possível enviar a mensagem.");
    } finally {
      setSending(false);
    }
  };

  // Ditado por voz (Web Speech API — nativo do navegador, sem dependência; melhor no Chrome).
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const SpeechRec: any =
    typeof window !== "undefined"
      ? // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition
      : null;
  const speechSupported = !!SpeechRec;
  const recognitionRef = useRef<{ stop: () => void } | null>(null);
  const [listening, setListening] = useState(false);

  useEffect(() => () => recognitionRef.current?.stop(), []);

  const toggleVoice = async () => {
    if (!SpeechRec) {
      setError("Ditado por voz não é suportado neste navegador. Use o Google Chrome.");
      return;
    }
    if (listening) {
      recognitionRef.current?.stop();
      return;
    }
    // No Chrome Android o SpeechRecognition costuma dar "not-allowed" mesmo com permissão;
    // pedir o microfone via getUserMedia primeiro registra o acesso e destrava o reconhecimento.
    try {
      if (navigator.mediaDevices?.getUserMedia) {
        const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
        stream.getTracks().forEach((t) => t.stop());
      }
    } catch {
      setError("Permita o acesso ao microfone no navegador para ditar por voz.");
      return;
    }
    try {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const rec: any = new SpeechRec();
      rec.lang = "pt-BR";
      rec.interimResults = true;
      rec.continuous = false;
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      rec.onresult = (e: any) => {
        let text = "";
        for (let i = 0; i < e.results.length; i++) text += e.results[i][0].transcript;
        setInput(text);
      };
      rec.onend = () => setListening(false);
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      rec.onerror = (e: any) => {
        setListening(false);
        const err = e?.error;
        if (err === "not-allowed" || err === "service-not-allowed") {
          setError("Permita o acesso ao microfone no navegador para ditar por voz.");
        } else if (err === "no-speech") {
          setError("Não captei sua voz. Tente novamente.");
        } else if (err !== "aborted") {
          setError("Falha no ditado por voz.");
        }
      };
      recognitionRef.current = rec;
      setError(null);
      setListening(true);
      rec.start();
    } catch {
      setListening(false);
      setError("Não foi possível iniciar o microfone.");
    }
  };

  // Clicar numa ação sugerida preenche o campo de mensagem (não envia — o usuário revisa e manda).
  const applyAction = (text: string) => {
    setInput(text);
    inputRef.current?.focus();
  };

  // Copia a resposta (sem o bloco de ações) para a área de transferência, com feedback transitório.
  const copyMessage = async (text: string, idx: number) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedIndex(idx);
      window.setTimeout(() => setCopiedIndex((c) => (c === idx ? null : c)), 1500);
    } catch {
      // clipboard indisponível (contexto não seguro/permita) — falha silenciosa
    }
  };

  const renderAssistant = (content: string, idx: number): React.ReactNode => {
    const { text, actions } = parseAssistant(content);
    return (
      <>
        {renderMarkdown(text)}
        <ActionChips actions={actions} onPick={applyAction} isMobile={isMobile} disabled={sending} />
        <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 0.25 }}>
          <Tooltip title={copiedIndex === idx ? "Copiado" : "Copiar resposta"}>
            <IconButton size="small" onClick={() => copyMessage(text, idx)} sx={{ color: colors.textSecondary, p: 0.25 }}>
              {copiedIndex === idx ? <Check sx={{ fontSize: "0.95rem" }} /> : <ContentCopy sx={{ fontSize: "0.95rem" }} />}
            </IconButton>
          </Tooltip>
        </Box>
      </>
    );
  };

  if (view === "conversations") {
    return (
      <Box sx={{ flex: 1, overflowY: "auto", p: 1 }}>
        <Box
          onClick={startNew}
          sx={{ display: "flex", alignItems: "center", gap: 1, p: 1.25, borderRadius: "10px", cursor: "pointer", color: colors.primary, bgcolor: "#eef7f8", "&:hover": { bgcolor: "#dff1f3" } }}
        >
          <Add fontSize="small" />
          <Typography sx={{ fontWeight: 600, fontSize: "0.9rem" }}>Nova conversa</Typography>
        </Box>
        <Box sx={{ m: 1, mt: 1.5, borderRadius: "8px", overflow: "hidden", border: "1px solid #f5c6cb" }}>
          <AuditNotice clinicName={clinicName} />
        </Box>
        {conversations.length === 0 ? (
          <Typography variant="body2" sx={{ color: colors.textSecondary, textAlign: "center", py: 3 }}>
            Nenhuma conversa ainda.
          </Typography>
        ) : (
          conversations.map((c) => (
            <Box
              key={c.id}
              onClick={() => openConversation(c)}
              sx={{ display: "flex", alignItems: "center", gap: 1.5, p: 1, borderRadius: "8px", cursor: "pointer", "&:hover": { bgcolor: "#f1f3f5" } }}
            >
              <ChatBubbleOutline sx={{ fontSize: 20, color: colors.textSecondary, flexShrink: 0 }} />
              <Box sx={{ minWidth: 0, flex: 1 }}>
                <Typography sx={{ fontSize: "0.85rem", color: colors.textPrimary }} noWrap>
                  {c.title || "Conversa"}
                </Typography>
                <Typography variant="caption" sx={{ color: colors.textSecondary }}>
                  {formatWhen(c.updatedAt)}
                </Typography>
              </Box>
            </Box>
          ))
        )}
      </Box>
    );
  }

  return (
    <>
      <AuditNotice clinicName={clinicName} />
      {/* Molde dos assistentes (linguagem única, 28/10): sem balão — quem fala numa linha, o texto embaixo */}
      <Box sx={{ flex: 1, overflowY: "auto", px: 2, py: 2, bgcolor: colors.white, display: "flex", flexDirection: "column", gap: 2.5 }}>
        {messages.length === 0 && !sending && (
          <Typography variant="body2" sx={{ color: colors.textSecondary, mt: 1 }}>
            Envie uma mensagem para começar.
          </Typography>
        )}
        {messages.map((m, i) => (
          <Box key={i} ref={i === messages.length - 1 ? lastMsgRef : undefined}>
            <SpeakerLabel isUser={m.role === "user"} initial={userInitial} name={m.role === "user" ? "Você" : agent.displayName} />
            <Box sx={{ fontSize: "0.9375rem", lineHeight: 1.55, color: colors.textStrong, wordBreak: "break-word" }}>
              {m.role === "assistant" ? renderAssistant(m.content, i) : <Box sx={{ whiteSpace: "pre-wrap" }}>{m.content}</Box>}
            </Box>
            {m.truncated && (
              <Box sx={{ mt: 0.75, px: 1, py: 0.5, bgcolor: "#fff8e1", border: "1px solid #ffe0a3", borderRadius: "6px" }}>
                <Typography sx={{ display: "block", fontSize: "0.75rem", color: "#8a6d00", lineHeight: 1.35 }}>
                  A resposta foi interrompida por atingir o limite de tamanho. Envie "continuar" para ver o restante.
                </Typography>
              </Box>
            )}
          </Box>
        ))}
        {sending && (
          <Box sx={{ alignSelf: "flex-start", display: "flex", alignItems: "center", gap: 1, color: colors.textSecondary }}>
            <CircularProgress size={14} sx={{ color: colors.primary }} />
            <Typography variant="caption">digitando…</Typography>
          </Box>
        )}
        {error && (
          <Box sx={{ alignSelf: "center", textAlign: "center", display: "flex", flexDirection: "column", alignItems: "center", gap: 0.5 }}>
            <Typography variant="caption" sx={{ color: expired ? "#8a6d00" : colors.error }}>
              {error}
            </Typography>
            {expired && <ActionChips actions={["Iniciar nova conversa"]} onPick={startNew} isMobile={isMobile} />}
          </Box>
        )}
        <div ref={listEndRef} />
      </Box>

      <Box sx={{ px: 1.5, pt: 1, pb: 1.5, bgcolor: colors.white }}>
        <ComposerFrame isMobile={isMobile} disabled={sending}>
          <InputBase
            value={input}
            inputRef={inputRef}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                send();
              }
            }}
            placeholder={`Pergunte a ${agent.displayName}…`}
            fullWidth
            multiline
            maxRows={4}
            disabled={sending}
            inputProps={{ maxLength: 4000, "aria-label": `Mensagem para ${agent.displayName}` }}
            sx={{ fontSize: "0.9375rem", lineHeight: 1.45, color: colors.textStrong, py: 0.5, pl: 1, "& textarea::placeholder": { color: colors.textSecondary, opacity: 1 } }}
          />
          {speechSupported && (
            <ComposerIconButton label={listening ? "Parar o ditado" : "Ditar"} onClick={toggleVoice} isMobile={isMobile}
              pressed={listening} tone="recording" disabled={sending}>
              <Mic />
            </ComposerIconButton>
          )}
          <SendButton onClick={send} disabled={sending || !input.trim()} isMobile={isMobile} />
        </ComposerFrame>
      </Box>
    </>
  );
});

AgentConversation.displayName = "AgentConversation";

export default AgentConversation;
